<h1 mat-dialog-title>Snack rewards for {{data.companyName}}</h1>
<div mat-dialog-content class="snack-rewards-dialog-content">
    <div class="setting-mode-panel">
        <div class="setting-mode-buttons">
            <button mat-stroked-button type="button" [class.active-mode]="mode === 'inherit'" (click)="setMode('inherit')">
                {{canInherit() ? 'Use parent setting' : 'Use brand defaults'}}
            </button>
            <button mat-stroked-button type="button" [class.active-mode]="mode === 'none'" (click)="setMode('none')">
                No snacks
            </button>
            <button mat-stroked-button type="button" [class.active-mode]="mode === 'custom'" (click)="setMode('custom')">
                Custom snacks
            </button>
        </div>
        <p class="setting-mode-help" *ngIf="mode === 'inherit' && canInherit()">
            This company will use snack reward selections from {{data.parentCompanyName || 'its parent company'}}.
        </p>
        <p class="setting-mode-help" *ngIf="mode === 'inherit' && !canInherit()">
            This company will use the default brand and campaign snack rewards.
        </p>
        <p class="setting-mode-help" *ngIf="mode === 'none'">
            This company explicitly overrides the parent setting and shows no snacks.
        </p>
        <p class="setting-mode-help" *ngIf="mode === 'custom'">
            Selected snacks are available to this company even when their brand or company campaign targeting differs.
        </p>
    </div>

    <div class="snack-import-helper" *ngIf="hasImportOptions()" [class.collapsed]="!isImportHelperExpanded">
        <button
            type="button"
            class="snack-import-helper-header"
            [attr.aria-expanded]="isImportHelperExpanded"
            (click)="toggleImportHelper()">
            <span class="snack-import-helper-title">Select snack rewards from existing brand or company</span>
            <span class="snack-import-helper-action">{{isImportHelperExpanded ? 'Hide' : 'Show'}}</span>
        </button>
        <div class="snack-import-helper-body" *ngIf="isImportHelperExpanded">
            <div class="snack-import-controls">
                <mat-form-field appearance="outline">
                    <mat-label>Source type</mat-label>
                    <mat-select
                        [(ngModel)]="importSourceType"
                        [disabled]="isImportLoading"
                        (ngModelChange)="onImportSourceTypeChange($event)">
                        <mat-option *ngIf="data.brandOptions.length > 0" value="brand">Existing brand</mat-option>
                        <mat-option *ngIf="data.companyOptions.length > 0" value="company">Existing company</mat-option>
                    </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline">
                    <mat-label>{{importSourceType === 'company' ? 'Company snack source' : 'Brand snack source'}}</mat-label>
                    <mat-select *ngIf="importSourceType === 'brand'" [(ngModel)]="selectedBrandId" [disabled]="isImportLoading">
                        <mat-option *ngFor="let brand of data.brandOptions" [value]="brand.id">
                            {{brand.label}} <span *ngIf="brand.subtitle">({{brand.subtitle}})</span>
                        </mat-option>
                    </mat-select>
                    <mat-select *ngIf="importSourceType === 'company'" [(ngModel)]="selectedCompanyId" [disabled]="isImportLoading">
                        <mat-option *ngFor="let company of data.companyOptions" [value]="company.id">
                            {{company.label}} <span *ngIf="company.subtitle">({{company.subtitle}})</span>
                        </mat-option>
                    </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline">
                    <mat-label>Apply behavior</mat-label>
                    <mat-select
                        [(ngModel)]="importApplyMode"
                        [disabled]="isImportLoading"
                        (ngModelChange)="setImportApplyMode($event)">
                        <mat-option value="replace">Replace current selection</mat-option>
                        <mat-option value="merge">Merge with current selection</mat-option>
                    </mat-select>
                </mat-form-field>
                <button
                    mat-stroked-button
                    type="button"
                    color="primary"
                    [disabled]="(importSourceType === 'brand' ? !selectedBrandId : !selectedCompanyId) || isImportLoading"
                    (click)="loadImportPreview()">
                    Preview Snacks
                </button>
                <mat-spinner *ngIf="isImportLoading" [diameter]="24"></mat-spinner>
            </div>
            <app-inlinemessage *ngIf="importError" level="warning" [message]="importError"></app-inlinemessage>
            <div class="snack-import-preview" *ngIf="importPreviewLoaded && !importError">
                <app-inlinemessage
                    *ngIf="importPreviewUnavailableRewardIds.length > 0"
                    level="warning"
                    message="Some saved snack IDs are no longer globally selectable and will be skipped.">
                </app-inlinemessage>
                <p class="snack-import-summary">
                    {{importPreviewRewardIds.length}} selectable snacks from {{importSourceLabel}}.
                    {{importPreviewAddedRewardIds.length}} would be added, {{importPreviewOverlappingRewardIds.length}} are already selected, and {{importPreviewRemovedRewardIds.length}} would be removed.
                </p>
                <div class="snack-import-diff">
                    <div>
                        <h3>Would Add</h3>
                        <p *ngIf="importPreviewAddedRewardIds.length === 0">No snacks</p>
                        <ul *ngIf="importPreviewAddedRewardIds.length > 0">
                            <li *ngFor="let rewardId of importPreviewAddedRewardIds; trackBy: trackRewardId">{{getRewardLabel(rewardId)}}</li>
                        </ul>
                    </div>
                    <div>
                        <h3>Already Selected</h3>
                        <p *ngIf="importPreviewOverlappingRewardIds.length === 0">No snacks</p>
                        <ul *ngIf="importPreviewOverlappingRewardIds.length > 0">
                            <li *ngFor="let rewardId of importPreviewOverlappingRewardIds; trackBy: trackRewardId">{{getRewardLabel(rewardId)}}</li>
                        </ul>
                    </div>
                    <div>
                        <h3>Would Remove</h3>
                        <p *ngIf="importPreviewRemovedRewardIds.length === 0">No snacks</p>
                        <ul *ngIf="importPreviewRemovedRewardIds.length > 0">
                            <li *ngFor="let rewardId of importPreviewRemovedRewardIds; trackBy: trackRewardId">{{getRewardLabel(rewardId)}}</li>
                        </ul>
                    </div>
                    <div *ngIf="importPreviewUnavailableRewardIds.length > 0">
                        <h3>Skipped</h3>
                        <ul>
                            <li *ngFor="let rewardId of importPreviewUnavailableRewardIds; trackBy: trackRewardId">
                                {{rewardId}} - Not found in the globally selectable snack catalog
                            </li>
                        </ul>
                    </div>
                </div>
                <button mat-raised-button type="button" color="accent" [disabled]="!canApplyImportPreview()" (click)="applyImportPreview()">
                    Apply To Custom Selection
                </button>
                <p class="snack-import-applied" *ngIf="importPreviewApplied">
                    Snack rewards were applied to the dialog selection. Use Done to save.
                </p>
            </div>
        </div>
    </div>

    <app-inlinemessage *ngIf="rewards.length === 0" level="warning" message="No globally selectable snack rewards are available."></app-inlinemessage>
    <div class="snack-reward-toolbar" *ngIf="rewards.length > 0">
        <div class="snack-reward-selection-controls">
            <mat-checkbox
                aria-label="Select or clear all globally selectable snack rewards"
                [checked]="isAllSelected()"
                [indeterminate]="isSomeSelected() && !isAllSelected()"
                [disabled]="!isCustomMode()"
                (change)="toggleAllRewards($event.checked)">
                Select all
            </mat-checkbox>
            <span>{{getSelectedCount()}} of {{rewards.length}} selected</span>
        </div>
        <div class="snack-reward-selection-controls">
            <mat-checkbox [checked]="showSelectedOnly" (change)="setShowSelectedOnly($event.checked)">
                Show selected only
            </mat-checkbox>
            <button mat-button type="button" [disabled]="!isCustomMode() || getSelectedCount() === 0" (click)="clearSelected()">
                Clear
            </button>
        </div>
    </div>
    <app-inlinemessage
        *ngIf="rewards.length > 0 && showSelectedOnly && displayedRewards.length === 0"
        level="warning"
        message="No selected snack rewards to show">
    </app-inlinemessage>
    <div class="snack-reward-list" [class.readonly-list]="!isCustomMode()" *ngIf="displayedRewards.length > 0">
        <div class="snack-reward-row" [class.selected-row]="isSelected(reward.id)" *ngFor="let reward of displayedRewards; trackBy: trackReward">
            <mat-checkbox
                class="snack-reward-checkbox"
                (change)="toggleReward(reward.id, $event.checked)"
                [checked]="isSelected(reward.id)"
                [disabled]="!isCustomMode()">
            </mat-checkbox>
            <div class="reward-text">
                <div class="reward-title">{{getRewardTitle(reward)}}</div>
                <div class="reward-subtitle">{{getRewardSubtitle(reward)}}</div>
            </div>
        </div>
    </div>
</div>
<div class="buttons snack-rewards-actions" mat-dialog-actions>
    <button mat-raised-button class="donebutton" color="primary" (click)="okClicked()">Done</button>
    <button mat-button (click)="onDismiss()">Cancel</button>
</div>
